// Copyright (c) Microsoft. All rights reserved.

import { useMsal } from '@azure/msal-react';
import { Button, Text, Textarea, Field, makeStyles, shorthands, tokens } from '@fluentui/react-components';
import { MicRegular, SendRegular } from '@fluentui/react-icons';
import debug from 'debug';
import * as speechSdk from 'microsoft-cognitiveservices-speech-sdk';
import React, { useState } from 'react';
import { Constants } from '../../Constants';
import { COPY } from '../../assets/strings';
import { AuthHelper } from '../../libs/auth/AuthHelper';
import { GetResponseOptions } from '../../libs/hooks/useChat';
import { AlertType } from '../../libs/models/AlertType';
import { ChatMessageType } from '../../libs/models/ChatMessage';
import { useAppDispatch, useAppSelector } from '../../redux/app/hooks';
import { RootState } from '../../redux/app/store';
import { addAlert } from '../../redux/features/app/appSlice';
import { editConversationInput, updateBotResponseStatus } from '../../redux/features/conversations/conversationsSlice';
import { Alerts } from '../shared/Alerts';
import { getErrorDetails } from '../utils/TextUtils';
import { SpeechService } from './../../libs/services/SpeechService';
import { updateUserIsTyping } from './../../redux/features/conversations/conversationsSlice';
import { ChatStatus } from './ChatStatus';
import { Fade } from '@fluentui/react-motion-components-preview';

const log = debug(Constants.debug.root).extend('chat-input');

const useClasses = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        maxWidth: '105em',
        ...shorthands.margin(tokens.spacingVerticalNone, tokens.spacingHorizontalM),
    },
    typingIndicator: {
        maxHeight: '28px',
    },
    alerts: {
        ...shorthands.margin('4px', 0),
    },
    content: {
        ...shorthands.gap(tokens.spacingHorizontalM),
        display: 'flex',
        flexDirection: 'row',
        width: '100%',
    },
    input: {
        width: '100%',
    },
    textarea: {
        maxHeight: '80px',
    },
    controls: {
        display: 'flex',
        flexDirection: 'row',
    },
    essentials: {
        display: 'flex',
        flexDirection: 'row',
        marginLeft: 'auto', // align to right
    },
    functional: {
        display: 'flex',
        flexDirection: 'row',
        marginRight: '24px',
    },
    dragAndDrop: {
        ...shorthands.border(tokens.strokeWidthThick, ' solid', tokens.colorBrandStroke1),
        ...shorthands.padding('8px'),
        textAlign: 'center',
        backgroundColor: tokens.colorNeutralBackgroundInvertedDisabled,
        fontSize: tokens.fontSizeBase300,
        color: tokens.colorBrandForeground1,
        caretColor: 'transparent',
    },
    inputLength: {
        lineHeight: '32px',
        paddingLeft: '6px',
    },
    listening: {
        userSelect: 'none',
        position: 'relative',
        width: 'fit-content',
        bottom: '48px',
        display: 'flex',
        flexDirection: 'row',
        ...shorthands.margin(0, 'auto'),
        lineHeight: tokens.fontSizeHero800,
        gap: tokens.spacingVerticalXS,
        ...shorthands.padding(
            tokens.spacingVerticalXS,
            tokens.spacingHorizontalL,
            tokens.spacingVerticalXS,
            tokens.spacingHorizontalXS,
        ),
        ...shorthands.border(tokens.strokeWidthThick, ' solid', tokens.colorBrandStroke2),
        borderRadius: tokens.borderRadiusCircular,
        backgroundColor: tokens.colorBrandBackground2,
        color: tokens.colorBrandStroke1,
        fontSize: tokens.fontSizeBase400,
    },
    listeningIcon: {
        fontSize: tokens.fontSizeHero800,
    },
});

interface ChatInputProps {
    onSubmit: (options: GetResponseOptions) => Promise<void>;
}

const MAX_INPUT_LENGTH = 1000;

export const ChatInput: React.FC<ChatInputProps> = ({ onSubmit }) => {
    const classes = useClasses();
    const { instance, inProgress } = useMsal();
    const dispatch = useAppDispatch();
    const { conversations, selectedId } = useAppSelector((state: RootState) => state.conversations);
    const { activeUserInfo } = useAppSelector((state: RootState) => state.app);

    const [value, setValue] = useState('');
    const [recognizer, setRecognizer] = useState<speechSdk.SpeechRecognizer>();
    const [isListening, setIsListening] = useState(false);
    const [inputError, setInputError] = useState<string | undefined>(undefined);

    const textAreaRef = React.useRef<HTMLTextAreaElement>(null);
    React.useEffect(() => {
        // Focus on the text area when the selected conversation changes
        textAreaRef.current?.focus();
    }, [selectedId]);

    React.useEffect(() => {
        async function initSpeechRecognizer() {
            const speechService = new SpeechService();
            const response = await speechService.getSpeechTokenAsync(
                await AuthHelper.getSKaaSAccessToken(instance, inProgress),
            );
            if (response.isSuccess) {
                const recognizer = speechService.getSpeechRecognizerAsyncWithValidKey(response);
                setRecognizer(recognizer);
            }
        }

        initSpeechRecognizer().catch((e) => {
            const errorDetails = getErrorDetails(e);
            const errorMessage = `Unable to initialize speech recognizer. Details: ${errorDetails}`;
            dispatch(addAlert({ message: errorMessage, type: AlertType.Error }));
        });
    }, [dispatch, instance, inProgress]);

    React.useEffect(() => {
        const chatState = conversations[selectedId];
        setValue(chatState.disabled ? COPY.CHAT_DELETED_MESSAGE() : chatState.input);
    }, [conversations, selectedId]);

    const handleSpeech = () => {
        if (recognizer) {
            setIsListening(true);
            recognizer.recognizeOnceAsync((result) => {
                if (result.reason === speechSdk.ResultReason.RecognizedSpeech) {
                    if (result.text && result.text.length > 0) {
                        dispatch(editConversationInput({ id: selectedId, newInput: result.text }));
                        textAreaRef.current?.focus();
                        //handleSubmit(result.text);
                    }
                }
                setIsListening(false);
            });
        }
    };

    const handleSubmit = (value: string, messageType: ChatMessageType = ChatMessageType.Message) => {
        if (value.trim() === '') {
            return; // only submit if value is not empty
        }

        if (value.length > MAX_INPUT_LENGTH) {
            setInputError(`Die maximale Länge von ${MAX_INPUT_LENGTH} Zeichen wurde überschritten. Nachricht kann nicht gesendet werden.`);
            return;
        }

        setValue('');
        dispatch(editConversationInput({ id: selectedId, newInput: '' }));
        dispatch(updateBotResponseStatus({ chatId: selectedId, status: 'Calling the kernel' }));
        onSubmit({ value, messageType, chatId: selectedId }).catch((error) => {
            const message = `Error submitting chat input: ${(error as Error).message}`;
            log(message);
            dispatch(
                addAlert({
                    type: AlertType.Error,
                    message,
                }),
            );
        });
    };

    const handleInputChange = (newValue: string) => {
        if (newValue.length > MAX_INPUT_LENGTH) {
            setValue(newValue);
            dispatch(editConversationInput({ id: selectedId, newInput: newValue }));
            setInputError(`Die maximale Länge von ${MAX_INPUT_LENGTH} Zeichen wurde überschritten.`);
        } else {
            setValue(newValue);
            dispatch(editConversationInput({ id: selectedId, newInput: newValue }));
            setInputError(undefined);
        }
    };

    return (
        <div className={classes.root}>
            <div style={{ position: 'relative', width: '100%', height: 0, overflow: 'visible' }}>
                <Fade visible={isListening}>
                    <div className={classes.listening}>
                        <MicRegular className={classes.listeningIcon} />
                        Nachricht einsprechen
                    </div>
                </Fade>
            </div>
            <div className={classes.typingIndicator}>
                <ChatStatus />
            </div>
            <div className={classes.alerts}>
                <Alerts />
            </div>
            <div className={classes.content}>
                <Field
                    validationState={inputError ? 'error' : 'none'}
                    validationMessage={inputError}
                    className={classes.input}
                >
                    <Textarea
                        title="Chat input"
                        aria-label="Chat input field. Click enter to submit input."
                        ref={textAreaRef}
                        id="chat-input"
                        resize="vertical"
                        disabled={conversations[selectedId].disabled}
                        textarea={{
                            className: classes.textarea,
                        }}
                        value={value}
                        onFocus={() => {
                            // update the locally stored value to the current value
                            const chatInput = document.getElementById('chat-input');
                            if (chatInput) {
                                setValue((chatInput as HTMLTextAreaElement).value);
                            }
                            // User is considered typing if the input is in focus
                            if (activeUserInfo) {
                                dispatch(
                                    updateUserIsTyping({ userId: activeUserInfo.id, chatId: selectedId, isTyping: true }),
                                );
                            }
                        }}
                        onChange={(_event, data) => {
                            handleInputChange(data.value);
                        }}
                        onKeyDown={(event) => {
                            if (event.key === 'Enter' && !event.shiftKey) {
                                event.preventDefault();
                                handleSubmit(value);
                            }
                        }}
                        onBlur={() => {
                            // User is considered not typing if the input is not  in focus
                            if (activeUserInfo) {
                                dispatch(
                                    updateUserIsTyping({ userId: activeUserInfo.id, chatId: selectedId, isTyping: false }),
                                );
                            }
                        }}
                    />
                </Field>
            </div>
            <div className={classes.controls}>
                <div className={classes.inputLength}>
                    <Text>
                        {value.length}/{MAX_INPUT_LENGTH}
                    </Text>
                </div>
                <div className={classes.essentials}>
                    {recognizer && (
                        <Button
                            appearance="transparent"
                            disabled={conversations[selectedId].disabled || isListening}
                            icon={<MicRegular />}
                            onClick={handleSpeech}
                        />
                    )}
                    <Button
                        title="Senden"
                        aria-label="Submit message"
                        appearance="transparent"
                        icon={<SendRegular />}
                        onClick={() => {
                            handleSubmit(value);
                        }}
                        disabled={conversations[selectedId].disabled || value.length > MAX_INPUT_LENGTH}
                    />
                </div>
            </div>
        </div>
    );
};
